const get = require('lodash/get')
const set = require('lodash/set')
const keyBy = require('lodash/keyBy')
const { listAll } = require('./list-all')

/**
 * Converts a list of product quantities into a generic format.
 * This function first fetches a complete list of all products and then uses a converter
 * to transform the given product quantities into a generic structure.
 *
 * @param {object} state - The application state or context required to list all products.
 * @param {Object<string, {number | object}>} productQuantities - An object with product IDs as keys and quantities as values.
 * @param {object} options - Options for conversion.
 * @param {string} [options.path=undefined] - Optional path to the quantity within the product quantities object.
 * @param {boolean} [options.addProductPrefix=false] - Whether to add a 'product:' prefix to generic product IDs.
 * @returns {Promise<Object<string, number>>} A promise that resolves to an object of generic product quantities.
 */
exports.toGenericQuantities = async (state, productQuantities, options) => {
  const allProducts = await listAll(state)
  return toGenericQuantitiesSync(state, productQuantities, allProducts, options)
}

/**
 * Converts a list of product quantities into a generic format.
 * This function first fetches a complete list of all products and then uses a converter
 * to transform the given product quantities into a generic structure.
 *
 * @param {object} state - The application state or context required to list all products.
 * @param {Object<string, {number | object}>} productQuantities - An object with product IDs as keys and quantities as values.
 * @param {array<object>} productQuantities - An object with product IDs as keys and quantities as values.
 * @param {object} options - Options for conversion.
 * @param {string} [options.path=undefined] - Optional path to the quantity within the product quantities object.
 * @param {boolean} [options.addProductPrefix=false] - Whether to add a 'product:' prefix to generic product IDs.
 * @returns {Promise<Object<string, number>>} A promise that resolves to an object of generic product quantities.
*/

const toGenericQuantitiesSync = (state, productQuantities, allProducts, options) => {
  if (!allProducts || allProducts.length === 0) {
    throw new Error('Product list is required to convert to generic quantities! (Try the async version of this function?)')
  }
  const productsById = keyBy(allProducts, '_id')

  const {path = undefined, addProductPrefix = false} = options || {}

  // This is to enable leaving the path parameter undefined or null
  const quantityPath = (topLevel) => {
    if (!path) {
      return topLevel
    }
    return `${topLevel}.${path}`
  }

  return Object.keys(productQuantities).reduce((acc, productId) => {
    const product = productsById[productId.toLowerCase()]
    const { genericParent, genericFactor } = product || {}
    if (!genericParent || !genericFactor) {
      return acc
    }

    const genericProductId = addProductPrefix
      ? `product:${genericParent}`.toLowerCase()
      : genericParent

    const currentQuantity = get(acc, quantityPath(genericProductId), 0)
    const additionalQuantity = get(productQuantities, quantityPath(productId), 0) * genericFactor

    // if there's no key created with the generic parent yet, we need to set it first, and we're gonna copy
    // the value from the original productQuantities before we update the quantity
    if (!acc[genericProductId]) {
      acc[genericProductId] = productQuantities[productId]
    }

    set(acc, quantityPath(genericProductId), currentQuantity + additionalQuantity)

    return acc
  }, {})
}
exports.toGenericQuantitiesSync = toGenericQuantitiesSync
